import { z } from 'zod';
import { paginationResponseSchema, paginationSchema, SortOrder, sortOrderSchema, } from './helpers';
import { ColumnDataTypes, FilterOperators, FiltersSchema, createTableQuerySchema, } from './table';
import { severitySchema } from './shared/severity';
import { endpointDetailsSchema } from './endpoints';
/*
  Contracts for the "Agents Activity → Packages & Repos" screen (KIR-3483).

  Read-only from the frontend's perspective: events are captured by the endpoint
  agent and extracted + persisted server-side in the scan pipeline (there is no
  client-facing ingest endpoint), so these contracts cover only the list / stats
  read APIs and the row shape the table + side panel render.
*/
/** Discriminates the two tabs: package installs vs git repo access. */
export const agentCommandEventTypeSchema = z.enum(['package', 'git']);
/** Git action captured from a git command. */
export const gitActionSchema = z.enum(['clone', 'pull', 'fetch', 'push']);
/**
 * One command event row. Package fields are populated for `event_type` =
 * 'package'; git fields for 'git'. `userEmail` is resolved server-side from the
 * owning user for the table's "User" column.
 */
export const agentCommandEventSchema = z.object({
    id: z.string(),
    eventType: agentCommandEventTypeSchema,
    timestamp: z.string(), // ISO-8601; "Last Seen" column
    userId: z.string(),
    userEmail: z.string().nullable(),
    agentName: z.string(),
    projectPath: z.string().nullable(),
    /**
     * Resolved projects-inventory entity (KIR-3483). Null when the command's
     * folder couldn't be linked (personal accounts, non-git folders without a
     * machine id). The table shows the name as a link to the single-project page
     * when present, else falls back to the folder path.
     */
    projectName: z.string().nullable(),
    projectSlug: z.string().nullable(),
    rawCommand: z.string(),
    knownBad: z.boolean(),
    /**
     * Endpoint (machine) this command ran on — the agent's machine_id (KIR-3833
     * item 7). Null for events ingested before the column or whose request omitted
     * the Kirin-Machine-Id header. The Occurrences table shows this as the Endpoint
     * ID column (matching EndpointsTable), preferring `endpointName` when resolved.
     */
    machineId: z.string().nullable(),
    /** Registry hostname for machineId when known, else null (id-only fallback). */
    endpointName: z.string().nullable(),
    // package fields (null for git)
    packageManager: z.string().nullable(),
    packageName: z.string().nullable(),
    packageVersion: z.string().nullable(),
    sourceUrl: z.string().nullable(),
    // git fields (null for package)
    repoUrl: z.string().nullable(),
    action: gitActionSchema.nullable(),
    branch: z.string().nullable(),
});
/**
 * Sortable columns (DoD: sort available for every column). Superset covering
 * both tabs; the frontend only offers the columns relevant to the active tab.
 */
export const agentCommandEventSortFieldSchema = z.enum([
    'timestamp',
    'userEmail',
    'agentName',
    'packageManager',
    'packageName',
    'packageVersion',
    'sourceUrl',
    'projectPath',
    'repoUrl',
    'action',
    'branch',
]);
/**
 * Per-column filters (DoD: every column filterable). Text columns are STRING —
 * a distinct-value multi-select dropdown (INCLUDES/EXCLUDES chips) plus a
 * free-text contains, matching the Projects/Inventory tables; timestamp is a
 * date filter. The keys are the single source of truth for allowed filter
 * fields (see createTableQuerySchema) and must match the DataTable column `id`s,
 * the backend filter field map, and AGENT_COMMAND_FILTER_OPTION_FIELDS.
 */
export const AGENT_COMMAND_COLUMN_DATA_TYPES = {
    packageName: ColumnDataTypes.STRING,
    packageVersion: ColumnDataTypes.STRING,
    packageManager: ColumnDataTypes.STRING,
    sourceUrl: ColumnDataTypes.STRING,
    repoUrl: ColumnDataTypes.STRING,
    action: ColumnDataTypes.STRING,
    branch: ColumnDataTypes.STRING,
    agentName: ColumnDataTypes.STRING,
    userEmail: ColumnDataTypes.STRING,
    projectPath: ColumnDataTypes.STRING,
    // Internal filter used by the aggregated-view Occurrences drill-down to pin
    // the unique project identity (not a visible column). See buildFilters.
    projectKey: ColumnDataTypes.STRING,
    // KIR-5098: endpoint name or machine ID (Occurrences drill-down search).
    // Not a visible column; Contains / Equals only (see the overrides below).
    endpoint: ColumnDataTypes.STRING,
    timestamp: ColumnDataTypes.DATE,
};
/**
 * Columns that expose a server-backed distinct-value dropdown (multi-select).
 * Every STRING column above — the timestamp date filter has no options list.
 */
export const AGENT_COMMAND_FILTER_OPTION_FIELDS = {
    packageName: 'packageName',
    packageVersion: 'packageVersion',
    packageManager: 'packageManager',
    sourceUrl: 'sourceUrl',
    repoUrl: 'repoUrl',
    action: 'action',
    branch: 'branch',
    agentName: 'agentName',
    userEmail: 'userEmail',
    endpointName: 'endpointName',
    projectPath: 'projectPath',
};
export const agentCommandFilterOptionFieldSchema = z.enum([
    'packageName',
    'packageVersion',
    'packageManager',
    'sourceUrl',
    'repoUrl',
    'action',
    'branch',
    'agentName',
    'userEmail',
    'endpointName',
    'projectPath',
]);
/**
 * Distinct-value dropdown query (org or personal). `eventType` scopes options to
 * the active tab so a column's dropdown only offers values that appear in that
 * tab's rows.
 */
export const agentCommandFilterOptionsQuerySchema = z.object({
    eventType: agentCommandEventTypeSchema,
    field: agentCommandFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Distinct-value dropdown response — plain strings (the render adds icons). */
export const agentCommandFilterOptionsResponseSchema = z.object({
    field: agentCommandFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
/**
 * Base list query (shared by the server query schema and the client params
 * schema). `from`/`to` are ISO datetimes the frontend derives from the
 * time-range dropdown. `agentNames` / `userIds` are comma-separated
 * multi-selects. `search` is a free-text match over package name, source, repo,
 * and project.
 */
const agentCommandEventsQueryBaseSchema = paginationSchema.extend({
    eventType: agentCommandEventTypeSchema, // which tab
    sortBy: agentCommandEventSortFieldSchema.default('timestamp'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    agentNames: z.string().optional(), // comma-separated
    userIds: z.string().optional(), // comma-separated
});
/**
 * Backend query schema. Handles bracket-notation filter params from Fastify
 * (`field[operator]=val`) or a pre-parsed filters array; validates each filter
 * field + operator against AGENT_COMMAND_COLUMN_DATA_TYPES.
 */
export const listAgentCommandEventsQuerySchema = createTableQuerySchema(agentCommandEventsQueryBaseSchema, AGENT_COMMAND_COLUMN_DATA_TYPES, { endpoint: [FilterOperators.CONTAINS, FilterOperators.INCLUDES] });
/**
 * API-client params. `page`/`limit` optional (backend defaults); `filters` is a
 * pre-parsed array serialized to bracket notation by serializeTableQueryParams.
 */
export const listAgentCommandEventsParamsSchema = z.object({
    eventType: agentCommandEventTypeSchema,
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: agentCommandEventSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    agentNames: z.string().optional(),
    userIds: z.string().optional(),
    filters: z.array(FiltersSchema.element).optional(),
});
/** Paginated list response. */
export const agentCommandEventsListSchema = z.object({
    events: z.array(agentCommandEventSchema),
    pagination: paginationResponseSchema,
});
/**
 * Summary stat cards. `blockedToday` is always 0 in the visibility-only
 * release (no enforcement) but is carried so the UI card and a future
 * enforcement ticket share one contract.
 */
export const agentCommandEventsStatsSchema = z.object({
    packageEvents24h: z.number().int().min(0),
    gitEvents24h: z.number().int().min(0),
    blockedToday: z.number().int().min(0),
    knownBadDetected24h: z.number().int().min(0),
    uniqueSourcesRepos7d: z.number().int().min(0),
});
/*
  ═══════════════════════════════════════════════════════════════════════════
  Aggregated Packages view (KIR-3807)

  The Packages tab collapses one-row-per-command into one row per
  (packageName + sourceUrl + project + packageVersion). Each row carries the
  distinct agents (multi-chip), a distinct-user count, an occurrence count, the
  most-recent timestamp, and OSV vulnerabilities for that package/version. Git
  is unaffected — this view is package-only.
  ═══════════════════════════════════════════════════════════════════════════
*/
/**
 * OSV summary for the "Vulnerabilities (OSV)" column (KIR-3805), enriched from
 * the Kirin OSV mirror and cached. `highest` is the top-severity record the
 * cell renders (link opens `osv.dev/vulnerability/<id>`); `count` drives the
 * "+N more" chip. `severityRank` (Malicious 5 → none 0) is the sort key.
 *
 * A `null` osv summary on a row means "not checked yet" → the cell shows
 * "Checking OSV…". `status:'error'` → "OSV unavailable". `status:'none'` →
 * "No known vulnerabilities".
 */
export const osvHighestSchema = z.object({
    id: z.string(),
    /** CVSS band; null only when the record has neither CVSS nor qualitative severity. */
    level: severitySchema.nullable(),
    /** 0–10 CVSS base score; null for malicious/score-less records. */
    score: z.number().nullable(),
    malicious: z.boolean(),
    fixAvailable: z.boolean(),
    published: z.string().nullable(), // ISO-8601
});
export const osvCellSummarySchema = z.object({
    status: z.enum(['none', 'vulnerable', 'error']),
    count: z.number().int().min(0),
    severityRank: z.number().int().min(0),
    /**
     * OSV ecosystem (npm, PyPI, …); lets the "+N more" chip deep-link to the full
     * osv.dev listing for the package (KIR-3833). Null when the manager is unmapped.
     */
    ecosystem: z.string().nullable(),
    highest: osvHighestSchema.nullable(),
});
/** One distinct user in an aggregated row's Users drill-down (KIR-3833). */
export const aggregatedUserSchema = z.object({
    /** Null for a user with no email on file (KIR-5281: still counted, so listed). */
    email: z.string().nullable(),
    /** When this user last ran the group's command (ISO-8601). */
    lastSeen: z.string(),
});
/** One aggregated package row. */
/** @see AggregatedPackage.listState */
export const PACKAGE_LIST_STATES = ['blocked', 'none'];
export const packageListStateSchema = z.enum(PACKAGE_LIST_STATES);
export const aggregatedPackageSchema = z.object({
    packageName: z.string().nullable(),
    packageVersion: z.string().nullable(),
    packageManager: z.string().nullable(),
    sourceUrl: z.string().nullable(),
    /**
     * Unique project identity (resolved project id, else raw folder path) — pins
     * the Occurrences drill-down so it can't collide with a same-named project.
     */
    projectKey: z.string().nullable(),
    /** Display value for the Project column: resolved name, else folder path. */
    projectPath: z.string().nullable(),
    projectName: z.string().nullable(),
    projectSlug: z.string().nullable(),
    /** Distinct agents in the group — rendered as chips. */
    agents: z.array(z.string()),
    /** COUNT(DISTINCT user) — the sortable/filterable "Users" column. */
    userCount: z.number().int().min(0),
    /** Distinct users (email + last-seen) — the Users drill-down (KIR-3833). */
    users: z.array(aggregatedUserSchema),
    /** COUNT(*) — the "Occurrences" column. */
    occurrenceCount: z.number().int().min(0),
    /** COUNT(DISTINCT machine) — the sortable/filterable "Endpoints" column. */
    endpointCount: z.number().int().min(0),
    /** Distinct endpoints — the clickable Endpoints drill-down (KIR-3833). */
    endpoints: z.array(endpointDetailsSchema),
    lastSeen: z.string(), // ISO-8601; default sort desc
    knownBad: z.boolean(),
    /** OSV summary (KIR-3805); null = not yet checked ("Checking OSV…"). */
    osv: osvCellSummarySchema.nullable(),
    /**
     * KIR-4443 (AC 8): whether this package is on the organization's
     * `packages-management` Allow list, Block list, or neither. `allowed` wins
     * when it is on both, matching the Allow > Block precedence the scanner
     * applies, so the page never claims a package is blocked that would in fact
     * be installed.
     *
     * A rule is `name` + `ecosystem`, but an aggregated row is
     * name + version + manager + source + project, so ONE Allow/Block action
     * flips the state of every row for that package — across versions, projects
     * and source URLs. That is deliberate: the rule is about the package.
     */
    listState: packageListStateSchema,
    /** KIR-4701: group-scoped lists only — whose choice `listState` is. */
    enforcementSource: z.enum(['group', 'organization']).optional(),
    /**
     * KIR-4443: the OSV ecosystem an Allow/Block rule for this row names,
     * derived from `packageManager` with the same mapping the backend matcher
     * uses. Unlike `osv.ecosystem` it is present before the OSV check has run,
     * so a brand-new install is actionable. Null when the manager is unmapped.
     */
    ecosystem: z.string().nullable(),
});
/**
 * Sortable columns for the aggregated view: the group-key columns plus the two
 * counts and last-seen. Vulnerabilities are enriched post-query (not in SQL),
 * so they are intentionally not sortable.
 */
export const aggregatedPackageSortFieldSchema = z.enum([
    'packageName',
    'packageVersion',
    'packageManager',
    'sourceUrl',
    'projectPath',
    'lastSeen',
    'occurrenceCount',
    'userCount',
    'endpointCount',
    'severity',
]);
/**
 * Filterable columns. Text group-key columns are STRING; `agents` and
 * `userCount` are ENUM (multi-select value list → INCLUDES/EXCLUDES over the
 * distinct set — `userCount` filters by user email, not by count, KIR-3875);
 * `occurrenceCount` is NUMBER (aggregate HAVING). `lastSeen` is a DATE range.
 * Vulnerabilities are not filterable (enriched post-query).
 */
export const AGGREGATED_PACKAGE_COLUMN_DATA_TYPES = {
    packageName: ColumnDataTypes.STRING,
    packageVersion: ColumnDataTypes.STRING,
    packageManager: ColumnDataTypes.STRING,
    sourceUrl: ColumnDataTypes.STRING,
    projectPath: ColumnDataTypes.STRING,
    agents: ColumnDataTypes.ENUM,
    // Filter by user email (value list), though the column displays/sorts a count.
    userCount: ColumnDataTypes.ENUM,
    occurrenceCount: ColumnDataTypes.NUMBER,
    // KIR-5098: filter by endpoint name or ID (Contains / Equals), though the
    // column displays/sorts a count.
    endpointCount: ColumnDataTypes.STRING,
    lastSeen: ColumnDataTypes.DATE,
};
/**
 * The Packages-page list-state filter (KIR-4443, AC 10). `all` applies no
 * filter and is the default. `blocked` is rows whose `listState` is
 * `blocked`; `allowed` means "not blocked" — every other row. Packages have
 * no Allow list, so no row is ever in an `allowed` state.
 *
 * Deliberately NOT a bracket-notation column filter like the others: those are
 * built from real SQL columns with a `/filter-options` endpoint behind them,
 * and `listState` is neither — it is derived per request from the org's rule
 * set. A dedicated scalar param keeps it out of the column-filter machinery
 * (and out of the filter-options endpoint, which has nothing to enumerate).
 */
export const PACKAGE_LIST_STATE_FILTERS = [
    'all',
    'allowed',
    'blocked',
];
export const packageListStateFilterSchema = z
    .enum(PACKAGE_LIST_STATE_FILTERS)
    .default('all');
const aggregatedPackagesQueryBaseSchema = paginationSchema.extend({
    sortBy: aggregatedPackageSortFieldSchema.default('lastSeen'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    listState: packageListStateFilterSchema,
    /** KIR-4701: resolve each package's state for this security group. */
    securityGroupId: z.string().uuid().optional(),
});
/** Backend query schema (bracket-notation filters validated against the map). */
/**
 * KIR-5098: the Endpoints filter's operators — Contains / Equals (UI), plus
 * Excludes and Is Empty / Is Not Empty kept for existing links. Anything else
 * would be accepted and silently ignored by the builder.
 */
const AGGREGATED_ENDPOINT_OPERATOR_OVERRIDES = {
    endpointCount: [
        FilterOperators.CONTAINS,
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
export const listAggregatedPackagesQuerySchema = createTableQuerySchema(aggregatedPackagesQueryBaseSchema, AGGREGATED_PACKAGE_COLUMN_DATA_TYPES, AGGREGATED_ENDPOINT_OPERATOR_OVERRIDES);
/** API-client params (pre-parsed filters array). */
export const listAggregatedPackagesParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: aggregatedPackageSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    listState: packageListStateFilterSchema.optional(),
    filters: z.array(FiltersSchema.element).optional(),
    /** KIR-4701: resolve each package's state for this security group. */
    securityGroupId: z.string().uuid().optional(),
});
/** Paginated aggregated-packages response. */
export const aggregatedPackagesListSchema = z.object({
    packages: z.array(aggregatedPackageSchema),
    pagination: paginationResponseSchema,
});
/*
  ─── Aggregated Repositories view (KIR-3807) ───
  Same aggregation as packages, grouped by (repoUrl + action + branch +
  project). No vulnerabilities (git access, not a package).
*/
/** One aggregated repository row. */
export const aggregatedRepositorySchema = z.object({
    repoUrl: z.string().nullable(),
    action: gitActionSchema.nullable(),
    branch: z.string().nullable(),
    /** Unique project identity (id-or-path) — pins the Occurrences drill-down. */
    projectKey: z.string().nullable(),
    projectPath: z.string().nullable(),
    projectName: z.string().nullable(),
    projectSlug: z.string().nullable(),
    agents: z.array(z.string()),
    userCount: z.number().int().min(0),
    /** Distinct users (email + last-seen) — the Users drill-down (KIR-3833). */
    users: z.array(aggregatedUserSchema),
    occurrenceCount: z.number().int().min(0),
    /** COUNT(DISTINCT machine) — the sortable/filterable "Endpoints" column. */
    endpointCount: z.number().int().min(0),
    /** Distinct endpoints — the clickable Endpoints drill-down (KIR-3833). */
    endpoints: z.array(endpointDetailsSchema),
    lastSeen: z.string(),
    knownBad: z.boolean(),
});
export const aggregatedRepositorySortFieldSchema = z.enum([
    'repoUrl',
    'action',
    'branch',
    'projectPath',
    'lastSeen',
    'occurrenceCount',
    'userCount',
    'endpointCount',
]);
export const AGGREGATED_REPOSITORY_COLUMN_DATA_TYPES = {
    repoUrl: ColumnDataTypes.STRING,
    action: ColumnDataTypes.STRING,
    branch: ColumnDataTypes.STRING,
    projectPath: ColumnDataTypes.STRING,
    agents: ColumnDataTypes.ENUM,
    // Filter by user email (value list), though the column displays/sorts a count.
    userCount: ColumnDataTypes.ENUM,
    occurrenceCount: ColumnDataTypes.NUMBER,
    // KIR-5098: filter by endpoint name or ID (Contains / Equals), though the
    // column displays/sorts a count.
    endpointCount: ColumnDataTypes.STRING,
    lastSeen: ColumnDataTypes.DATE,
};
const aggregatedRepositoriesQueryBaseSchema = paginationSchema.extend({
    sortBy: aggregatedRepositorySortFieldSchema.default('lastSeen'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const listAggregatedRepositoriesQuerySchema = createTableQuerySchema(aggregatedRepositoriesQueryBaseSchema, AGGREGATED_REPOSITORY_COLUMN_DATA_TYPES, AGGREGATED_ENDPOINT_OPERATOR_OVERRIDES);
export const listAggregatedRepositoriesParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: aggregatedRepositorySortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    filters: z.array(FiltersSchema.element).optional(),
});
export const aggregatedRepositoriesListSchema = z.object({
    repositories: z.array(aggregatedRepositorySchema),
    pagination: paginationResponseSchema,
});
//# sourceMappingURL=agent-command-events.js.map